/**
 * /case-studies/ai-chatbot-testing
 *
 * TWO SOURCES, reconciled:
 *
 * - `ai-testing-use-cases.md` — the client's rewrite. Supplies the query-form
 *   headings, the SEO metadata, and the editorial judgement about what the page
 *   is arguing.
 * - The live page, supplied as screenshots. Carries substance the rewrite
 *   dropped: the Overview, the pipeline's per-step detail, the pass/fail
 *   outcomes, the sub-points under items 1 and 2, and several closing lines.
 *
 * Where they overlap, the rewrite wins on wording — it is the later document and
 * was written to fix the original. Where the original has content the rewrite
 * simply omits, the original is restored. Every string here comes from one of
 * the two; nothing is composed.
 *
 * ONE DELIBERATE EXCEPTION. The original's Results read "Significant reduction
 * in undetected chatbot errors" and "Faster iteration cycles". The rewrite
 * quotes those exact phrases as the vagueness the voice guide bans, so the
 * rewrite's wording is kept and the figures stay outstanding. See `results`.
 *
 * Decisions taken with the client: these live at /case-studies/{slug} rather
 * than /ai-testing-use-cases/{slug}; Results ships without a placeholder block.
 */

import type { IconName } from "../../components/ui/waq/Icon";

export const seo = {
  title: "AI Chatbot Testing at Scale — LLM Evaluation in CI",
  description:
    "How we test conversational AI: multi-turn scenarios, LLM-as-a-judge evaluation, adversarial prompts, and regression detection on every model change.",
} as const;

export const hero = {
  breadcrumb: { label: "Case Studies", href: "/case-studies" },
  /** The original's own label for this page. */
  badge: "Use Case #1",
  heading: "End-to-End Testing of AI Chatbots at Scale",
  lead: "Traditional test automation asserts that output equals an expected value.",
  leadTurn: "Chatbots don't have one.",
  body: "Ask the same question twice and you get two different sentences that mean the same thing. A conventional test fails both times — or passes something wrong because the wording happened to match.",
} as const;

/** Restored from the live page. The rewrite omits this section entirely. */
export const overview = {
  display: "Overview",
  paragraphs: [
    "Modern AI chatbots are no longer simple Q&A systems — they are complex, stateful conversational agents capable of handling multi-turn dialogues, reasoning tasks, and dynamic user interactions. Traditional testing approaches fail in this context because chatbot outputs are non-deterministic, context-dependent, and highly sensitive to prompt variations.",
    "To address this, we implemented a comprehensive AI chatbot testing framework that validates conversational quality, robustness, and reliability across real-world scenarios.",
  ],
} as const;

export const breaks = {
  heading: "How do you test an AI chatbot?",
  display: "Why chatbots break traditional testing",
  /** The live page's own framing for the five properties. */
  challengeLabel: "The Challenge",
  intro: "AI chatbot systems introduce several critical testing challenges:",
  columns: { property: "Property", effect: "What it does to your tests" },
  rows: [
    {
      property: "Non-deterministic outputs",
      effect: "The same input may produce different responses",
    },
    {
      property: "Context dependency",
      effect: "Each response depends on the full conversation history",
    },
    {
      property: "Multi-turn complexity",
      effect: "Errors may only appear after several dialogue steps",
    },
    {
      property: "Model variability",
      effect: "Different AI models behave inconsistently across the same scenario",
    },
    {
      property: "Hidden failures",
      effect: "Hallucinations, bias, or logical inconsistencies are hard to detect manually",
    },
  ],
  highlightIndex: 4,
  /** The rewrite's commentary, then the original's conclusion. Both, in order. */
  commentary:
    "That last row is the dangerous one. A chatbot returning a confident, fluent, entirely invented answer passes every conventional check you have.",
  close:
    "Standard QA approaches with fixed expected outputs are insufficient for validating such systems.",
} as const;

/**
 * The pipeline diagram, rebuilt from the live page in real text. The original is
 * a picture; this is readable by a crawler, a model and a screen reader.
 */
export const pipeline = {
  display: "Our approach",
  lead: "We built a scalable, automated evaluation pipeline designed specifically for AI chatbots.",
  title: "Automated AI Chatbot Quality Assurance",
  subtitle: "Validating AI responses at scale — across any chatbot, any language model",
  nodes: [
    {
      step: "Step 1",
      label: "Test Scenarios",
      points: [
        "Realistic user conversations",
        "Multi-step dialogue flows",
        "Edge cases & stress tests",
      ],
    },
    {
      step: "Step 2",
      label: "AI Chatbot",
      points: ["Processes each scenario", "Generates responses", "Any model or provider"],
    },
    {
      step: "Step 3",
      label: "AI Quality Evaluator",
      points: ["Scores every response", "Detects quality issues", "AI judges AI outputs"],
    },
    {
      step: "Result",
      label: "Quality Report",
      points: ["Pass / Fail per scenario", "Regression tracking", "CI/CD integration"],
    },
  ],
  dimensionsLabel: "Quality dimensions measured",
  dimensions: [
    "Accuracy",
    "Relevance",
    "Consistency",
    "Truthfulness",
    "Safety",
    "Bias Detection",
  ],
  band: {
    lead: "Works across any AI chatbot — switch models freely without losing quality visibility",
    note: "Runs continuously in delivery pipelines · Scales to thousands of conversations · No manual review needed",
  },
  outcomes: [
    {
      verdict: "Pass",
      body: "Response quality above threshold",
      note: "Safe to ship",
      good: true,
    },
    {
      verdict: "Fail",
      body: "Quality regression detected",
      note: "Pipeline blocked — review required",
      good: false,
    },
  ],
  caption: "AI Chatbot Testing Pipeline",
  altText:
    "A four-stage pipeline. Test scenarios feed the AI chatbot; its responses go to an AI quality evaluator, which scores them across accuracy, relevance, consistency, truthfulness, safety and bias detection; the result is a quality report wired into CI. A pass means the response is above threshold and safe to ship; a fail means a regression was detected and the pipeline is blocked.",
} as const;

export type Approach = {
  title: string;
  body: string;
  points: string[];
  icon: IconName;
};

export const approach = {
  items: [
    {
      title: "Multi-Turn Conversation Testing",
      body: "We test full conversational flows, not just isolated prompts.",
      points: [
        "Simulate real user interactions across multiple steps",
        "Validate context retention and logical consistency",
        "Group conversations into scenarios so results are reproducible",
      ],
      icon: "sequence",
    },
    {
      title: "Model-Agnostic Testing",
      body: "We validate chatbot behavior across multiple AI models:",
      points: [
        "Compare responses from different AI providers",
        "Detect regressions when switching models or prompts",
        "Ensure consistent quality regardless of the AI backend",
      ],
      icon: "layers",
    },
    {
      title: "Automated Evaluation with AI-as-a-Judge",
      body: "Instead of hardcoded assertions, we use AI-based evaluation.",
      points: [],
      icon: "brain",
    },
    {
      title: "Metrics-Driven Validation",
      body: "50+ research-backed metrics covering answer relevancy, faithfulness, hallucination detection, coherence, task completion, and toxicity.",
      points: [],
      icon: "barChart",
    },
    {
      title: "Automated Framework",
      body: "CI/CD integration with structured test cases, synthetic conversation generation, and multi-turn evaluation support.",
      points: [],
      icon: "gears",
    },
    {
      title: "Adversarial Testing",
      body: "Prompt injection scenarios, ambiguous inputs, long conversations, and invalid queries to identify vulnerabilities.",
      points: [],
      icon: "shieldCheck",
    },
    {
      title: "Continuous Testing",
      body: "Tests run on every model/prompt change with instant regression detection and automated reporting.",
      points: [],
      icon: "infinity",
    },
  ] as Approach[],
  exampleAfterIndex: 2,
} as const;

/**
 * The AI-judge comparison, restored to the live page's fuller version. The
 * rewrite calls this the single most quotable passage on the site.
 */
export const example = {
  title: "AI-Powered Quality Evaluation: How It Works",
  subtitle: "Using AI to evaluate AI — moving beyond rigid rules to genuine understanding",
  question: "How do I reset my password?",
  answer: "Click Settings, then Security, then Reset.",
  fixed: {
    label: "Old Way: Fixed Rules",
    storedLabel: "Expected answer stored",
    expected: "Go to Settings > Security > Reset Password",
    verdict: "Fails — wording is different",
    note: "Even though the answer is correct!",
    listLabel: "What goes wrong",
    items: [
      "Correct answers rejected due to phrasing",
      "Must rewrite tests after every model update",
      "Cannot catch hallucinations or wrong facts",
      "Thousands of manual rules hard to maintain",
    ],
  },
  judge: {
    label: "New Way: AI Judge",
    reads: "AI judge reads both and understands the meaning",
    verdict: "Passes — same meaning recognized",
    note: "Works across paraphrasing and rewording",
    listLabel: "What you gain",
    items: [
      "Understands meaning, not just wording",
      "Tests stay valid when model is updated",
      "Catches wrong facts and invented answers",
      "Runs at scale — thousands per hour",
    ],
  },
  close:
    "The AI judge runs automatically in your delivery pipeline — every chatbot update is evaluated before reaching users",
  caption: "LLM-as-a-Judge: Non-Deterministic Output Evaluation",
  askedLabel: "Customer asks",
  saidLabel: "Chatbot says",
} as const;

export const results = {
  display: "Results",
  /**
   * Not rendered. Kept as the record of what is outstanding — the client asked
   * for the on-page placeholder to be removed.
   */
  gap: "Real figures for this section. How many scenarios per run? What evaluation throughput? What did a prompt-change cycle take before, and after?",
  claimsLabel: "Currently claimed, pending figures",
  claims: [
    "Reduction in undetected chatbot errors",
    "Faster iteration on prompt and model updates",
    "Improved response consistency across scenarios",
    "Earlier detection of hallucination and logical failure",
  ],
  /**
   * Scope, not outcomes. Every figure is read off the copy above — "50+
   * research-backed metrics", the six dimensions the pipeline measures, and the
   * four adversarial categories. NO FIGURE HERE WAS INVENTED, and none is a
   * measured client result, so none is a metric under the closed-list rule.
   */
  scopeLabel: "What runs on every change",
  scope: [
    {
      value: "50+",
      label: "Research-backed metrics",
      note: "Answer relevancy, faithfulness, hallucination detection, coherence, task completion, toxicity",
    },
    {
      value: "6",
      label: "Quality dimensions measured",
      note: "Accuracy, relevance, consistency, truthfulness, safety, bias detection",
    },
    {
      value: "4",
      label: "Adversarial categories",
      note: "Prompt injection, ambiguous inputs, long conversations, invalid queries",
    },
  ],
} as const;

export const applies = {
  display: "Where This Applies",
  items: [
    "Customer support chatbots",
    "AI assistants and copilots",
    "RAG (Retrieval-Augmented Generation) systems",
    "Enterprise AI tools",
  ],
} as const;

export const related = {
  display: "Related",
  items: [
    {
      label: "Testing On-Device AI in a Privacy-First Mobile Application",
      href: "/case-studies/on-device-ai-testing",
    },
    {
      label: "AI-Powered Test Generation for Enterprise Test Automation",
      href: "/case-studies/ai-test-generation",
    },
    { label: "AI-Driven Validation", href: "/services/ai-driven-validation" },
  ],
} as const;
